import {
  Button,
  DataTable,
  EmptyState,
  ErrorState,
  Skeleton,
  StatPill,
  toast,
} from '@sitewise/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Boxes, Sparkles } from 'lucide-react';
import { useState } from 'react';

import {
  archiveDeviceType,
  listDeviceTypes,
  seedDefaultDeviceTypes,
  type DeviceTypeSummary,
} from '../../../api/compliance.js';
import { ApiError } from '../../../lib/api-client.js';

import { CreateDeviceTypeSheet } from './create-device-type-sheet.js';
import { cadenceLabel } from './lib.js';

export function ComplianceDeviceTypesPage() {
  const queryClient = useQueryClient();
  const [openCreate, setOpenCreate] = useState(false);

  const typesQuery = useQuery({
    queryKey: ['device-types', { all: true }],
    queryFn: () => listDeviceTypes({ includeInactive: true }),
  });

  const seedMutation = useMutation({
    mutationFn: seedDefaultDeviceTypes,
    onSuccess: (res) => {
      toast.success(
        `Added ${res.result.inserted} default device types${
          res.result.skipped > 0 ? ` · ${res.result.skipped} skipped` : ''
        }.`,
      );
      void queryClient.invalidateQueries({ queryKey: ['device-types'] });
    },
    onError: (err) =>
      toast.error(err instanceof ApiError ? err.message : 'Could not seed defaults'),
  });

  async function handleArchive(t: DeviceTypeSummary) {
    if (!confirm(`Archive "${t.name}"? Devices already using this type must be archived first.`))
      return;
    try {
      await archiveDeviceType(t.id);
      toast.success('Type archived.');
      void queryClient.invalidateQueries({ queryKey: ['device-types'] });
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not archive');
    }
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm text-fg-secondary">
            Catalog of device kinds. Compliance-tracked types schedule inspections automatically;
            inventory-only types just store devices for reference.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            size="sm"
            variant="outline"
            onClick={() => seedMutation.mutate()}
            disabled={seedMutation.isPending}
          >
            <Sparkles className="h-4 w-4" />
            Seed defaults
          </Button>
          <Button size="sm" onClick={() => setOpenCreate(true)}>
            New type
          </Button>
        </div>
      </div>

      {typesQuery.isError ? (
        <ErrorState description="Could not load device types." />
      ) : typesQuery.isLoading ? (
        <div className="space-y-2">
          {Array.from({ length: 6 }).map((_, i) => (
            <Skeleton key={i} className="h-12 w-full" />
          ))}
        </div>
      ) : (typesQuery.data?.deviceTypes.length ?? 0) === 0 ? (
        <EmptyState
          icon={Boxes}
          title="No device types"
          description="Seed the defaults to get started, then customize for your portfolio."
          action={
            <Button
              size="sm"
              onClick={() => seedMutation.mutate()}
              disabled={seedMutation.isPending}
            >
              <Sparkles className="h-4 w-4" />
              Seed defaults
            </Button>
          }
        />
      ) : (
        <DataTable<DeviceTypeSummary>
          rowKey={(r) => r.id}
          rows={typesQuery.data!.deviceTypes}
          rowClassName={(r) => (r.isActive ? undefined : 'opacity-50')}
          columns={[
            {
              id: 'name',
              header: 'Name',
              description: 'The kind of equipment, such as a boiler or an elevator.',
              cell: (r) => (
                <div>
                  <div className="font-medium">{r.name}</div>
                  <div className="text-xs text-fg-muted">{r.slug}</div>
                </div>
              ),
            },
            {
              id: 'tracking',
              header: 'Tracking',
              description: 'Whether devices of this type generate compliance deadlines, or are kept as inventory only.',
              cell: (r) =>
                r.isComplianceTracked ? (
                  <StatPill value="Compliance" tone="primary" />
                ) : (
                  <StatPill value="Inventory only" tone="neutral" />
                ),
            },
            {
              id: 'cadence',
              header: 'Default cadence',
              description: 'How often new devices of this type are inspected, unless a device sets its own schedule.',
              cell: (r) => (
                <span className="text-sm">
                  {cadenceLabel(r.defaultCadence, r.defaultCustomIntervalDays)}
                </span>
              ),
            },
            {
              id: 'grace',
              header: 'Grace (days)',
              description: 'How many days past the due date a record can sit before it counts as overdue.',
              cell: (r) => <span className="tabular-nums">{r.defaultGracePeriodDays}</span>,
            },
            {
              id: 'actions',
              header: '',
              cell: (r) => (
                <div className="flex justify-end">
                  {r.isActive ? (
                    <Button
                      size="sm"
                      variant="ghost"
                      className="text-danger hover:text-danger"
                      onClick={() => handleArchive(r)}
                    >
                      Archive
                    </Button>
                  ) : (
                    <StatPill value="Archived" tone="neutral" />
                  )}
                </div>
              ),
              className: 'text-right',
            },
          ]}
        />
      )}

      {openCreate ? (
        <CreateDeviceTypeSheet
          onClose={() => setOpenCreate(false)}
          onCreated={() => {
            setOpenCreate(false);
            void queryClient.invalidateQueries({ queryKey: ['device-types'] });
          }}
        />
      ) : null}
    </div>
  );
}
